import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../api';
import { Play, CheckCircle, Sparkles, Layers, Flame, GlassWater, Clock, Award, Smile, Frown, AlertCircle, Pizza } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import './PizzaTestPlay.css';
import { getInitialStudentInfo, checkAutoRegister } from '../utils/queryPrefill';

const DEFAULT_PIZZAS = [
  {
    hash: 'margherita',
    title: '🍕 Класична Маргарита',
    description: 'Зберіть традиційну неаполітанську Маргариту з інгредієнтів нижче та оберіть правильний супровід.',
    crust: 'thin',
    sauce: 'tomato',
    toppings: ['mozzarella', 'basil', 'tomatoes'],
    drink: 'water',
    timeLimit: 2
  },
  {
    hash: 'pepperoni',
    title: '🍕 Гостра Пепероні',
    description: 'Тонка піца на томатній основі з пікантною ковбасою та ідеальним поєднанням з пивом/колою.',
    crust: 'thin',
    sauce: 'tomato',
    toppings: ['mozzarella', 'pepperoni'],
    drink: 'beer',
    timeLimit: 2
  },
  {
    hash: 'quattro-formaggi',
    title: '🍕 Чотири Сири',
    description: 'Зберіть фірмову Quattro Formaggi на вершковому соусі та підберіть для гостя напій.',
    crust: 'thick',
    sauce: 'white',
    toppings: ['mozzarella', 'parmesan', 'gorgonzola', 'dorblue'],
    drink: 'cider',
    timeLimit: 3
  }
];

const DEFAULT_INGREDIENTS = [
  { id: 'mozzarella', name: 'Моцарела', emoji: '🧀', color: '#fcd34d' },
  { id: 'parmesan', name: 'Пармезан', emoji: '🧀', color: '#fef08a' },
  { id: 'gorgonzola', name: 'Горгонзола', emoji: '🧀', color: '#cbd5e1' },
  { id: 'dorblue', name: 'Дорблю', emoji: '🧀', color: '#e2e8f0' },
  { id: 'pepperoni', name: 'Пепероні', emoji: '🍕', color: '#ef4444' },
  { id: 'prosciutto', name: 'Прошуто', emoji: '🥩', color: '#f87171' },
  { id: 'chicken', name: 'Курка', emoji: '🍗', color: '#fbbf24' },
  { id: 'bacon', name: 'Бекон', emoji: '🥓', color: '#fca5a5' },
  { id: 'mushrooms', name: 'Печериці', emoji: '🍄', color: '#e4e4e7' },
  { id: 'basil', name: 'Базилік', emoji: '🌿', color: '#4ade80' },
  { id: 'tomatoes', name: 'Томати', emoji: '🍅', color: '#f87171' },
  { id: 'pineapple', name: 'Ананас', emoji: '🍍', color: '#fef08a' },
  { id: 'olives', name: 'Оливки', emoji: '🫒', color: '#65a30d' },
  { id: 'pepper', name: 'Перець', emoji: '🫑', color: '#22c55e' }
];

const DEFAULT_CRUSTS = [
  { id: 'thin', name: 'Тонке', desc: 'Традиційне італійське' },
  { id: 'thick', name: 'Пухке', desc: 'Пухке неаполітанське' },
  { id: 'wholewheat', name: 'Цільнозернове', desc: 'Корисне та дієтичне' }
];

const DEFAULT_SAUCES = [
  { id: 'tomato', name: 'Томатний', emoji: '🍅', color: '#ef4444' },
  { id: 'white', name: 'Вершковий', emoji: '🥛', color: '#f3f4f6' },
  { id: 'pesto', name: 'Песто', emoji: '🌿', color: '#22c55e' },
  { id: 'bbq', name: 'Барбекю', emoji: '🍖', color: '#78350f' }
];

const DEFAULT_DRINKS = [
  { id: 'beer', name: 'Світле пиво / Лагер', emoji: '🍺' },
  { id: 'dark_beer', name: 'Темне пиво', emoji: '🍺' },
  { id: 'cider', name: 'Яблучний сидр', emoji: '🍏' },
  { id: 'cola', name: 'Кока-Кола', emoji: '🥤' },
  { id: 'lemonade', name: 'Лимонад', emoji: '🍋' },
  { id: 'orange_juice', name: 'Апельсиновий сік', emoji: '🍊' },
  { id: 'water', name: 'Вода негазована', emoji: '💧' },
  { id: 'sparkling_water', name: 'Вода газована', emoji: '🫧' }
];

// Helper to generate coordinates inside a circle
const generateRandomCoordinates = (count) => {
  const points = [];
  let attempts = 0;
  
  while (points.length < count && attempts < 100) {
    const angle = Math.random() * Math.PI * 2;
    const distance = 0.22 + Math.random() * 0.46; // percentage from center
    const x = 50 + Math.cos(angle) * distance * 100;
    const y = 50 + Math.sin(angle) * distance * 100;
    
    const tooClose = points.some(p => Math.hypot(p.x - x, p.y - y) < 12);
    if (!tooClose) {
      points.push({ x, y, rotation: Math.random() * 360 });
    }
    attempts++;
  }
  
  while (points.length < count) {
    points.push({ x: 40 + Math.random() * 20, y: 40 + Math.random() * 20, rotation: Math.random() * 360 });
  }
  
  return points;
};

const PizzaTestPlay = () => {
  const { hash } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { error, success } = useToast();
  
  const [pizza, setPizza] = useState(null);
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [availableDrinks, setAvailableDrinks] = useState([]);
  const [availableCrusts, setAvailableCrusts] = useState([]);
  const [availableSauces, setAvailableSauces] = useState([]);
  const [stage, setStage] = useState('intro'); // 'intro' | 'playing' | 'completed'
  
  // Game Play selections
  const [selectedCrust, setSelectedCrust] = useState('thin');
  const [selectedSauce, setSelectedSauce] = useState('tomato');
  const [activeToppings, setActiveToppings] = useState([]);
  const [selectedDrink, setSelectedDrink] = useState('water');
  
  // Results & Scoring
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState([]);
  const [missedToppings, setMissedToppings] = useState([]);
  const [extraToppings, setExtraToppings] = useState([]);
  const [issuedCertificateId, setIssuedCertificateId] = useState(null);
  
  // Timer State
  const [timeLeft, setTimeLeft] = useState(120);
  const timerRef = useRef(null);

  // Registration & profile prefill
  const [isRegistered, setIsRegistered] = useState(false);
  const [studentInfo, setStudentInfo] = useState(() =>
    getInitialStudentInfo(location.search)
  );
  const [availableCities, setAvailableCities] = useState([]);
  const [startTime] = useState(Date.now());

  // Lazy coordinates generator ref
  const toppingPositionsRef = useRef({});

  // Load pizza, available ingredients and drinks config matching hash
  useEffect(() => {
    const fetchData = async () => {
      let loadedPizza = null;
      try {
        const [pizzaRes, assetsRes] = await Promise.all([
          axios.get(`${API_URL}/pizza-templates/templates/play/${hash}${location.search}`),
          axios.get(`${API_URL}/pizza-templates/assets`)
        ]);
        
        loadedPizza = pizzaRes.data;
        setPizza(pizzaRes.data);
        if (pizzaRes.data?.drink) {
          setSelectedDrink(pizzaRes.data.drink);
        }
        if (pizzaRes.data?.crust) {
          setSelectedCrust(pizzaRes.data.crust);
        }
        if (pizzaRes.data?.sauce) {
          setSelectedSauce(pizzaRes.data.sauce);
        }
        setAvailableIngredients(assetsRes.data.toppings || []);
        setAvailableDrinks(assetsRes.data.drinks || []);
        setAvailableCrusts(assetsRes.data.crusts || []);
        setAvailableSauces(assetsRes.data.sauces || []);
      } catch (err) {
        console.error('Error fetching pizza data for play:', err);
        // Fallback to local defaults
        const found = DEFAULT_PIZZAS.find(p => p.hash === hash) || DEFAULT_PIZZAS[0];
        loadedPizza = found;
        setPizza(found);
        if (found?.drink) {
          setSelectedDrink(found.drink);
        }
        if (found?.crust) {
          setSelectedCrust(found.crust);
        }
        if (found?.sauce) {
          setSelectedSauce(found.sauce);
        }
        setAvailableIngredients(DEFAULT_INGREDIENTS);
        setAvailableDrinks(DEFAULT_DRINKS);
        setAvailableCrusts(DEFAULT_CRUSTS);
        setAvailableSauces(DEFAULT_SAUCES);
      }

      await checkLoggedInOrQuery(loadedPizza);
    };

    const fetchCities = async () => {
      try {
        const res = await axios.get(`${API_URL}/cities`);
        setAvailableCities(res.data);
      } catch (err) {
        console.error('Error fetching cities:', err);
      }
    };
    fetchCities();

    const checkLoggedInOrQuery = async (currentPizza) => {
      let currentInfo = getInitialStudentInfo(location.search);
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const res = await axios.get(`${API_URL}/students/me/dashboard`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const studentObj = res.data?.student || res.data?.summary;
          if (studentObj) {
            currentInfo = {
              firstName: studentObj.studentName || currentInfo.firstName || '',
              lastName: studentObj.studentLastName || currentInfo.lastName || '',
              city: studentObj.studentCity || currentInfo.city || '',
              position: studentObj.position || currentInfo.position || 'waiter'
            };
          }
        } catch (err) {
          console.error('Error fetching student profile:', err);
        }
      }

      const boundCity = currentPizza?.targetCity || '';
      const cityBindingEnabled = Boolean(boundCity);
      const mergedInfo = {
        ...currentInfo,
        city: boundCity || currentInfo.city || ''
      };

      setStudentInfo(mergedInfo);
      if (checkAutoRegister(mergedInfo, cityBindingEnabled)) {
        setIsRegistered(true);
      }
    };

    fetchData();
  }, [hash, location.search]);

  // Keep selected drink state in sync with available drinks list to avoid select state issues
  useEffect(() => {
    if (availableDrinks.length > 0) {
      const drinkExists = availableDrinks.some(d => d.id === selectedDrink);
      if (!drinkExists) {
        setSelectedDrink(availableDrinks[0].id);
      }
    }
  }, [availableDrinks, selectedDrink]);

  const handleSubmit = useCallback(async (isTimeExpired = false) => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    if (isTimeExpired) {
      error('Час вичерпано! Оцінку розраховано на основі поточної збірки.');
    }

    // SCORING LOGIC
    let computedScore = 100;
    const reports = [];
    
    // 1. Crust (20%)
    if (selectedCrust !== pizza.crust) {
      computedScore -= 20;
      const expectedCrustObj = availableCrusts.find(c => c.id === pizza.crust);
      const expectedCrust = expectedCrustObj ? expectedCrustObj.name : pizza.crust;
      const actualCrustObj = availableCrusts.find(c => c.id === selectedCrust);
      const actualCrust = actualCrustObj ? actualCrustObj.name : selectedCrust;
      reports.push(`🥖 Невірний тип тіста. Ви обрали "${actualCrust}", хоча за рецептом потрібно "${expectedCrust}". (-20%)`);
    }

    // 2. Sauce (20%)
    if (selectedSauce !== pizza.sauce) {
      computedScore -= 20;
      const expectedSauceObj = availableSauces.find(s => s.id === pizza.sauce);
      const expectedSauce = expectedSauceObj ? expectedSauceObj.name : pizza.sauce;
      const actualSauceObj = availableSauces.find(s => s.id === selectedSauce);
      const actualSauce = actualSauceObj ? actualSauceObj.name : selectedSauce;
      reports.push(`🍅 Невірний базовий соус. Ви обрали "${actualSauce}", а потрібно "${expectedSauce}". (-20%)`);
    }

    // 3. Toppings (40%)
    const expectedToppings = pizza.toppings || [];
    const missed = expectedToppings.filter(t => !activeToppings.includes(t));
    const extra = activeToppings.filter(t => !expectedToppings.includes(t));
    
    setMissedToppings(missed);
    setExtraToppings(extra);

    if (missed.length > 0) {
      const missedNames = missed.map(id => availableIngredients.find(i => i.id === id)?.name || id).join(', ');
      const penalty = Math.min(40, missed.length * 15);
      computedScore -= penalty;
      reports.push(`❌ Ви забули додати важливі інгредієнти: ${missedNames}. (-${penalty}%)`);
    }

    if (extra.length > 0) {
      const extraNames = extra.map(id => availableIngredients.find(i => i.id === id)?.name || id).join(', ');
      const penalty = Math.min(40, extra.length * 10);
      computedScore -= penalty;
      reports.push(`⚠️ Ви додали зайві інгредієнти, яких немає в рецепті: ${extraNames}. (-${penalty}%)`);
    }

    // 4. Drink Pairing (20%)
    if (selectedDrink !== pizza.drink) {
      computedScore -= 20;
      const expectedDrinkObj = availableDrinks.find(d => d.id === pizza.drink);
      const expectedDrink = expectedDrinkObj ? `${expectedDrinkObj.name} ${expectedDrinkObj.emoji}` : pizza.drink;
      reports.push(`🥤 Невдала рекомендація напою. Ви запропонували інший варіант, а найкращим вибором було: ${expectedDrink}. (-20%)`);
    }

    const finalScore = Math.max(0, computedScore);
    setScore(finalScore);
    setFeedback(reports);
    setStage('completed');
    
    if (finalScore === 100) {
      success('Чудово! Ви ідеально приготували піцу та підібрали напій!');
    }

    try {
      const res = await axios.post(`${API_URL}/pizza-templates/templates/play/${hash}/submit`, {
        studentName: studentInfo.firstName,
        studentLastName: studentInfo.lastName,
        studentCity: studentInfo.city,
        studentPosition: studentInfo.position,
        externalId: studentInfo.externalId,
        score: finalScore,
        percentage: finalScore,
        passed: finalScore >= (pizza.minScoreToPass || 80),
        details: {
          crust: selectedCrust,
          sauce: selectedSauce,
          toppings: activeToppings,
          drink: selectedDrink
        },
        feedback: reports,
        webhookUrl: sessionStorage.getItem('serviq_webhook_url') || '',
        timeSpent: Math.round((Date.now() - startTime) / 1000)
      });

      if (res.data && res.data.issuedCertificateId) {
        setIssuedCertificateId(res.data.issuedCertificateId);
      }
    } catch (err) {
      console.error('Error saving pizza scenario result:', err);
    }
  }, [selectedCrust, selectedSauce, activeToppings, selectedDrink, pizza, availableIngredients, availableDrinks, availableCrusts, availableSauces, error, success, studentInfo, hash, startTime]);

  // Handle Timer
  useEffect(() => {
    if (stage === 'playing') {
      const duration = (pizza?.timeLimit || 2) * 60;
      setTimeLeft(duration);
      
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit(true); // Auto-submit when time expires
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [stage, pizza, handleSubmit]);

  const handleStart = () => {
    setSelectedCrust('thin');
    setSelectedSauce('tomato');
    setActiveToppings([]);
    setSelectedDrink(pizza?.drink || 'water');
    setStage('playing');
  };

  const handleToppingClick = (id) => {
    if (activeToppings.includes(id)) {
      setActiveToppings(activeToppings.filter(t => t !== id));
    } else {
      setActiveToppings([...activeToppings, id]);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  if (!pizza) return <div className="pizza-play-loading">Завантаження конфігурації...</div>;

  if (!isRegistered) {
    return (
      <div className="registration-container">
        <div className="registration-card">
          <h2>Реєстрація на тренування</h2>
          <p>Рецепт: <strong>{pizza.title}</strong></p>
          <form onSubmit={(e) => { e.preventDefault(); setIsRegistered(true); }}>
            <div className="form-group">
              <label>Ім'я</label>
              <input type="text" value={studentInfo.firstName}
                onChange={(e) => setStudentInfo({ ...studentInfo, firstName: e.target.value })}
                placeholder="Введіть ім'я" required />
            </div>
            <div className="form-group">
              <label>Прізвище</label>
              <input type="text" value={studentInfo.lastName}
                onChange={(e) => setStudentInfo({ ...studentInfo, lastName: e.target.value })}
                placeholder="Введіть прізвище" required />
            </div>
            <div className="form-group">
              <label>Місто</label>
              <select value={studentInfo.city}
                onChange={(e) => setStudentInfo({ ...studentInfo, city: e.target.value })}
                required>
                <option value="">Оберіть місто...</option>
                {availableCities.map(c => (
                  <option key={c._id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Посада</label>
              <input type="text" value={studentInfo.position}
                onChange={(e) => setStudentInfo({ ...studentInfo, position: e.target.value })}
                placeholder="Введіть посаду" required />
            </div>
            <button type="submit" className="btn-start-test">
              Продовжити
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="pizza-play-page">
      <div className="pizza-play-container">
        
        {/* Intro Screen */}
        {stage === 'intro' && (
          <div className="pizza-card-view intro-card">
            <header className="intro-header">
              <h1>🍕 {pizza.title}</h1>
              <p className="scenario-desc">{pizza.description}</p>
            </header>

            <div className="task-brief-box">
              <h3>📋 Правила симуляції:</h3>
              <p>
                Ваше завдання — вивчити рецептуру піци, зібрати її на правильній основі,
                змастити правильним соусом та покласти необхідну кількість топінгів.
                Наприкінці обов'язково порекомендуйте гостю напій, який ідеально пасує до цієї піци.
              </p>
              
              <div className="hints-box">
                <strong style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'center' }}><Award size={16} /> Оцінювання здійснюється за критеріями:</strong>
                <ul>
                  <li>🥖 Правильність вибору товщини тіста (20%)</li>
                  <li>🍅 Відповідність базового соусу рецепту (20%)</li>
                  <li>🧀 Наявність усіх топінгів та відсутність зайвих (40%)</li>
                  <li>🍷 Правильна рекомендація напою (pairing) (20%)</li>
                </ul>
              </div>
            </div>

            <div className="timer-badge-intro" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <Clock size={16} />
              <span>Час на виконання: <strong>{pizza.timeLimit} хв</strong></span>
            </div>

            <div>
              <button className="btn-start-play" onClick={handleStart}>
                <Play size={20} />
                <span>Почати приготування</span>
              </button>
            </div>
          </div>
        )}

        {/* Play Screen */}
        {stage === 'playing' && (
          <div className="pizza-play-layout">
            
            {/* Left: Dynamic Visual Pizza Canvas */}
            <div className="pizza-visual-canvas-card">
              <div className="visual-canvas-header">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Pizza size={18} /> Візуальна дошка збірки</h3>
                <div className="play-timer" style={{ color: timeLeft < 30 ? '#ef4444' : 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Clock size={16} />
                  <span>{formatTime(timeLeft)}</span>
                </div>
              </div>

              <div className="pizza-board">
                <div className="pizza-pan">
                  {/* Crust shape depending on thin/thick/wholewheat */}
                  <div className={`pizza-crust ${selectedCrust}`}>
                    
                    {/* Sauce Layer overlaying the crust */}
                    <div className={`pizza-sauce-layer ${selectedSauce}`} />

                    {/* Render Scattered Toppings */}
                    {activeToppings.map(toppingId => {
                      const ingredient = availableIngredients.find(i => i.id === toppingId);
                      
                      // Dynamically generate coordinate markers for this topping if not present
                      if (!toppingPositionsRef.current[toppingId]) {
                        toppingPositionsRef.current[toppingId] = generateRandomCoordinates(6);
                      }
                      const positions = toppingPositionsRef.current[toppingId];
                      
                      return (
                        <React.Fragment key={toppingId}>
                          {positions.map((pos, idx) => (
                            <div
                              key={idx}
                              className="scattered-topping-item"
                              style={{
                                left: `${pos.x}%`,
                                top: `${pos.y}%`,
                                transform: `translate(-50%, -50%) rotate(${pos.rotation}deg)`,
                                color: ingredient?.color || '#ffffff'
                              }}
                            >
                              <span className="topping-emoji">{ingredient?.emoji || '🍕'}</span>
                            </div>
                          ))}
                        </React.Fragment>
                      );
                    })}

                  </div>
                </div>
              </div>

              <div className="pizza-canvas-footer">
                <div className="pizza-recipe-summary">
                  <strong>Поточна збірка:</strong> Тісто: <span>{availableCrusts.find(c => c.id === selectedCrust)?.name || selectedCrust}</span> | Соус: <span style={{ textTransform: 'capitalize' }}>{availableSauces.find(s => s.id === selectedSauce)?.name || selectedSauce}</span>
                </div>
              </div>
            </div>

            {/* Right: Interactive Recipe Controllers */}
            <div className="pizza-controller-card">
              <h3>Інгредієнти та напої</h3>

              {/* 1. Crust Selector */}
              <div className="control-section">
                <label className="section-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Layers size={16} /> 1. Оберіть тип тіста (основу)</label>
                <div className="options-row">
                  {availableCrusts.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      className={`option-card ${selectedCrust === item.id ? 'active' : ''}`}
                      onClick={() => setSelectedCrust(item.id)}
                    >
                      <span style={{ fontSize: '1.2rem', marginBottom: '0.2rem' }}>{item.emoji}</span>
                      <strong>{item.name}</strong>
                    </button>
                  ))}
                </div>
              </div>

              {/* 2. Sauce Selector */}
              <div className="control-section">
                <label className="section-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Flame size={16} /> 2. Змастіть піцу соусом</label>
                <div className="options-row">
                  {availableSauces.map(item => {
                    let indicatorColor = item.color || 'var(--accent-blue)';
                    const emoji = item.emoji;
                    const name = item.name.toLowerCase();
                    if (item.color) {
                      indicatorColor = item.color;
                    } else if (emoji === '🍅' || emoji === '🥫' || name.includes('томат') || name.includes('червон')) {
                      indicatorColor = '#ef4444';
                    } else if (emoji === '🥛' || emoji === '🧀' || name.includes('вершк') || name.includes('біл')) {
                      indicatorColor = '#f3f4f6';
                    } else if (emoji === '🌿' || emoji === '🌱' || name.includes('песто') || name.includes('зелен')) {
                      indicatorColor = '#22c55e';
                    } else if (emoji === '🍖' || emoji === '🔥' || emoji === '🫙' || name.includes('барбекю') || name.includes('bbq')) {
                      indicatorColor = '#78350f';
                    }

                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`option-card sauce ${selectedSauce === item.id ? 'active' : ''}`}
                        onClick={() => setSelectedSauce(item.id)}
                      >
                        <span className="sauce-indicator" style={{ backgroundColor: indicatorColor }} />
                        <span style={{ flexShrink: 0 }}>{item.emoji}</span>
                        <strong>{item.name}</strong>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 3. Toppings Checklist Grid */}
              <div className="control-section">
                <label className="section-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Pizza size={16} /> 3. Додайте топінги (клікніть щоб висипати)</label>
                <div className="toppings-selection-grid">
                  {availableIngredients.map(item => {
                    const isActive = activeToppings.includes(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`topping-select-card ${isActive ? 'active' : ''}`}
                        onClick={() => handleToppingClick(item.id)}
                      >
                        <span className="topping-emoji-large">{item.emoji || '🍕'}</span>
                        <span className="topping-name-label">{item.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 4. Drink Pairing Selector */}
              <div className="control-section">
                <label className="section-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><GlassWater size={16} /> 4. Порекомендуйте правильний напій</label>
                <div className="toppings-selection-grid">
                  {availableDrinks.map(d => {
                    const isActive = selectedDrink === d.id;
                    return (
                      <button
                        key={d.id}
                        type="button"
                        className={`topping-select-card ${isActive ? 'active' : ''}`}
                        onClick={() => setSelectedDrink(d.id)}
                      >
                        <span className="topping-emoji-large">{d.emoji || '🥤'}</span>
                        <span className="topping-name-label">{d.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Action Submit */}
              <button className="btn-submit-pizza" onClick={() => handleSubmit(false)}>
                <CheckCircle size={20} />
                <span>Завершити та перевірити</span>
              </button>

            </div>

          </div>
        )}

        {/* Results Screen */}
        {stage === 'completed' && (
          <div className="pizza-card-view result-card">
            
            {score === 100 && (
              <div className="confetti-holder">
                <Sparkles size={48} className="sparkle-icon" />
              </div>
            )}

            <header className="result-header">
              <CheckCircle size={64} className={`result-icon ${score >= 80 ? 'success' : score >= 50 ? 'warning' : 'danger'}`} />
              <h1>Тест завершено!</h1>
              <p>Оцінка правильності збірки та рекомендацій напоїв.</p>
            </header>

            <div className="score-summary-box">
              <div className="score-circle-wrapper">
                <div className={`score-circle ${score >= 80 ? 'passed' : score >= 50 ? 'warning' : 'failed'}`}>
                  <span className="score-number">{score}%</span>
                  <span className="score-label"><Award size={12} /> Оцінка</span>
                </div>
              </div>

              <div className="score-details-list">
                {feedback.length === 0 ? (
                  <div className="perfect-score-badge">
                    <Smile size={32} className="perfect-score-icon" />
                    <span>🌟 Ідеальний результат! Ви бездоганно запам'ятали рецептуру піци та підібрали ідеальне поєднання напоїв згідно з меню.</span>
                  </div>
                ) : (
                  <div className="feedback-errors-box">
                    <h4><AlertCircle size={16} /> Виявлені помилки:</h4>
                    <ul>
                      {feedback.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Toppings Summary Cards */}
            <div className="toppings-feedback-summary">
              <div className="topping-stat-card">
                <strong>Правильно зібрані топінги:</strong>
                <div className="tags-container">
                  {activeToppings.filter(t => pizza.toppings.includes(t)).map(tid => {
                    const ing = availableIngredients.find(i => i.id === tid);
                    return <span key={tid} className="tag-badge green">✓ {ing?.name}</span>;
                  })}
                  {activeToppings.length === 0 && <span className="empty-tag-msg">Немає топінгів</span>}
                </div>
              </div>

              {missedToppings.length > 0 && (
                <div className="topping-stat-card">
                  <strong className="text-danger-flex"><Frown size={14} /> Забуті інгредієнти:</strong>
                  <div className="tags-container">
                    {missedToppings.map(tid => {
                      const ing = availableIngredients.find(i => i.id === tid);
                      return <span key={tid} className="tag-badge red">✗ {ing?.name}</span>;
                    })}
                  </div>
                </div>
              )}

              {extraToppings.length > 0 && (
                <div className="topping-stat-card">
                  <strong className="text-warning-flex"><AlertCircle size={14} /> Зайві інгредієнти:</strong>
                  <div className="tags-container">
                    {extraToppings.map(tid => {
                      const ing = availableIngredients.find(i => i.id === tid);
                      return <span key={tid} className="tag-badge yellow">✗ {ing?.name}</span>;
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="result-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {issuedCertificateId && (
                <a
                  className="btn-restart"
                  style={{ background: 'linear-gradient(135deg, #eab308, #ca8a04)', color: '#fff', textDecoration: 'none', border: 'none' }}
                  href={`/verify-certificate/${issuedCertificateId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Award size={18} />
                  <span>📜 Переглянути сертифікат</span>
                </a>
              )}
              {score >= (pizza?.minScoreToPass || 80) ? (
                <button className="btn-restart" onClick={() => navigate('/student')}>
                  <CheckCircle size={18} />
                  <span>Повернутися до кабінету</span>
                </button>
              ) : (
                <button className="btn-restart" style={{ background: '#ef4444' }} onClick={() => navigate('/student')}>
                  <AlertCircle size={18} />
                  <span>Тест не складено (Завершити)</span>
                </button>
              )}
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default PizzaTestPlay;
